import { useEffect, useId, useRef, useState } from 'react';
import { api, type SessionRow } from '../lib/api';
import { prettyAgentTitle } from '../lib/agentName';
import { LiveIcon } from './LiveIcon';
import { Prism } from './Prism';

interface Props {
  open: boolean;
  onClose: () => void;
  onPick: (session_id: string) => void | Promise<void>;
  activeId?: string;
}

/**
 * Past sessions browser.
 *
 * Phase 5c. Lists the most recent 30 sessions returned by GET /api/sessions.
 * Tap a row to switch into that session. The Console handles the actual
 * loading via api.getSession() and resets local state.
 *
 * Layout pattern: bottom-sheet at all breakpoints. Mirrors AgentSheet's
 * scrim, dismissal, and safe-area handling so the two surfaces feel like
 * one drawer pattern. Open lazy-fetches; closing does not unmount.
 */
export function SessionsSheet({ open, onClose, onPick, activeId }: Props) {
  const [rows, setRows] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickingId, setPickingId] = useState<string | null>(null);
  const headingId = useId();
  const triggerRef = useRef<Element | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      const t = triggerRef.current as HTMLElement | null;
      if (t && typeof t.focus === 'function') t.focus();
      triggerRef.current = null;
      return;
    }
    triggerRef.current = document.activeElement;
    const first = panelRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    first?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .listSessions()
      .then((res) => {
        if (cancelled) return;
        setRows(res.sessions);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'failed to load sessions');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  return (
    <div
      className={
        'simpliloquy-sheet-scrim fixed inset-0 z-50 transition-opacity ' +
        (open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none')
      }
      aria-hidden={!open}
    >
      <button
        type="button"
        aria-label="Close sessions panel"
        tabIndex={-1}
        className="absolute inset-0 bg-night-900/60 backdrop-blur-[1px]"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className={
          'simpliloquy-sheet absolute left-0 right-0 bottom-0 bg-night-900 border-t border-night-600 ' +
          'rounded-t-2xl flex flex-col transition-transform ' +
          'md:left-1/2 md:right-auto md:-translate-x-1/2 md:bottom-8 md:w-[520px] md:rounded-2xl md:border md:shadow-2xl ' +
          (open ? 'translate-y-0' : 'translate-y-full md:translate-y-[110%]')
        }
        style={{
          height: '75dvh',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <div className="md:hidden flex items-center justify-center pt-2 pb-1">
          <div
            className="h-1 w-9 rounded-full"
            style={{ background: 'var(--agent-edge)' }}
          />
        </div>
        <div className="h-14 flex items-center px-4 border-b border-night-700">
          <h2 id={headingId} className="font-semibold text-sm text-paper-100">Past sessions</h2>
          <div className="flex-1" />
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="w-11 h-11 flex items-center justify-center rounded-md
                       hover:bg-night-800 focus:outline-none focus:bg-night-800"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && rows.length === 0 && (
            <div className="px-4 py-6 text-paper-400 text-sm">Loading.</div>
          )}
          {!loading && error && (
            <div className="px-4 py-6 text-paper-300 text-sm" role="alert">
              {error}
            </div>
          )}
          {!loading && !error && rows.length === 0 && (
            <div className="px-4 py-6 text-paper-400 text-sm">
              No past sessions yet.
            </div>
          )}
          {rows.map((s) => (
            <SessionRowView
              key={s.id}
              row={s}
              active={s.id === activeId}
              picking={pickingId === s.id}
              onSelect={async () => {
                if (pickingId) return;
                setPickingId(s.id);
                try {
                  await onPick(s.id);
                  onClose();
                } finally {
                  setPickingId(null);
                }
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

interface RowProps {
  row: SessionRow;
  active: boolean;
  picking: boolean;
  onSelect: () => void;
}

function SessionRowView({ row, active, picking, onSelect }: RowProps) {
  const isLive = row.closed_at == null;
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={picking}
      aria-current={active ? 'page' : undefined}
      className={
        'simpliloquy-button-focus w-full text-left flex items-center gap-3 px-4 h-16 ' +
        'relative hover:bg-night-800 transition-colors border-b border-night-800/60 ' +
        'disabled:opacity-60 disabled:cursor-progress'
      }
      style={{
        background: active ? 'var(--agent-bg-soft)' : undefined,
      }}
    >
      {active && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-2 bottom-2 w-0.5 rounded-r-sm"
          style={{ background: 'var(--agent-accent)' }}
        />
      )}
      <Prism slug={row.agent_active} size={20} active={active} />
      <div className="min-w-0 flex-1 flex flex-col leading-tight">
        <span className="text-[14px] text-paper-100 truncate font-semibold flex items-center gap-2">
          <span className="truncate">{prettyAgentTitle(row.agent_active)}</span>
          {isLive && (
            <span
              aria-label="Live session"
              className="inline-flex items-center"
            >
              <LiveIcon size={8} />
            </span>
          )}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-mono-wide text-paper-400 truncate">
          {picking ? 'loading.' : `${prettyTime(row.opened_at)} · ${row.device_label} · ${row.id.slice(0, 8)}`}
        </span>
      </div>
    </button>
  );
}

function prettyTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return `today ${time}`;
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} ${time}`;
}

function CloseIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M3 3 L13 13 M13 3 L3 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
